import { Routes, Route } from "react-router-dom";
import { useIsAuthenticated, useSignOut } from "react-auth-kit";
import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { AccessTime } from "@mui/icons-material";
import localize from "./common/localize";
import { clearPersistedPageIndexes } from "./hooks/usePersistedState";
import useIdleTimeout from "./hooks/useIdleTimeout";
import Clients from "./pages/client/Clients";
import Home from "./pages/Home";
import Events from "./pages/event/Events";
import EventStatus from "./pages/eventStatus/EventStatus";
import FormFields from "./pages/dynamicFormFields/FormFieldList";
import WorkFlow from "./pages/workflow/WorkFlow";
import Header from "./components/Header";
import OperationalArea from "./pages/operationalArea/OperationalArea";
import EventType from "./pages/eventType/EventType";
import NoMatch from "./components/NoMatch";
import DynamicForm from "./pages/dynamicForm/DynamicForm";
import Contractor from "./pages/contractor/Contractor";
import Provider from "./pages/Provider/Provider";
import Personal from "./pages/personal/Personal";
import VehicleType from "./pages/vehicleType/VehicleType";
import Vehicle from "./pages/vehicle/Vehicle";
import TeamsList from "./pages/team/TeamsList";
import Resolutions from "./pages/resolution/Resolutions";
import MeasurementUnitList from "./pages/measurement_units/MeasurementUnitList";
import SupplyList from "./pages/supplies/SupplyList";
import AdditionalNewnessList from "./pages/additionalNewness/AdditionalNewnessList";
import NewnessList from "./pages/newness/NewnessList";
import EventDetail from "./pages/event/EventDetail";
import Login from "./pages/auth/Login";
import Profiles from "./pages/profiles/Profiles";
import Users from "./pages/users/Users";
import Position from "./pages/position/Position";
import EvidenceImageViewer from "./pages/evidence/EvidenceImageViewer";
import DynamicFormFieldValueImageViewer from "./pages/dynamicForm/components/review/DynamicFormFieldValueImageViewer";
import Report from "./pages/event/components/Report";
import Proceeding from "./pages/proceeding/Proceeding";
import Warehouse from "./pages/warehouse/Warehouse";
import PaymentTypes from "./pages/paymentTypes/PaymentTypes";
import AdditionalCostTypes from "./pages/additionalCostTypes/AdditionalCostTypes";
import PreOrders from "./pages/preOrders/PreOrders";
import EvidenceTemplate from "./pages/evidenceTemplate/EvidenceTemplate";
import Payments from "./pages/payments/Payments";
import Bill from "./pages/relatedBills/Bill";
import DailyRoutes from "./pages/dailyRoute/DailyRoutes";
import { PrintDailyRoute } from "./pages/dailyRoute/components/PrintDailyRoute";
import InventoryMovementType from "./pages/inventory/movementType/InventoryMovementType";
import InventoryMovement from "./pages/inventory/InventoryMovement/InventoryMovement";
import FormBuilder from "./pages/dynamicForm/components/FormBuilder";
import RequestTypes from "./pages/requestTypes/RequestTypes";
import GeneralRequests from "./pages/generalRequests/GeneralRequests";
import ReportTypes from "./pages/reportTypes/ReportTypes";
import BillCreateEdit from "./pages/relatedBills/BillCreateEdit";
import FinishConcept from "./pages/finishConcept/FinishConcept";
import SupplyRequestList from "./pages/supplies/SupplyRequestList";
import AdditionalCostRequestList from "./pages/additionalCosts/AdditionalCostRequestList";
import PaymentToTechnicianRequestList from "./pages/payments/PaymentToTechnicianRequestList";
import SettlementList from "./pages/settlement/SettlementList";
import SettlementDetail from "./pages/settlement/SettlementDetail";
import SettlementReport from "./pages/settlement/SettlementReport";
import SettlementReportAll from "./pages/settlement/SettlementReportAll";
import ModuleGuard from "./components/common/ModuleGuard";

function App() {
  const isAuthenticated = useIsAuthenticated();
  const signOut = useSignOut();
  const navigate = useNavigate();
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false);
  useIdleTimeout();

  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;

        // 403 (Forbidden) = the session is valid but the user lacks
        // permission for this specific resource. Do NOT sign out or
        // redirect; let the calling code handle it.
        if (status === 403) {
          return Promise.reject(error);
        }

        // 401 (Unauthorized) = the session/token is invalid or expired.
        // Sign out and show the inactivity modal before the login form.
        if (status === 401) {
          try {
            clearPersistedPageIndexes();
            signOut();
          } catch {
            // ignore
          }
          if (window.location.pathname !== "/login") {
            setShowSessionExpiredModal(true);
          }
          // Swallow the rejection so it does not surface as an uncaught
          // error in callers that lack their own try/catch. The app is
          // already tearing down the session and redirecting to login,
          // so this promise is intentionally left unsettled.
          return new Promise(() => {});
        }
        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, [navigate, signOut]);

  const handleSessionExpiredAccept = () => {
    setShowSessionExpiredModal(false);
    navigate("/login", { replace: true });
  };

  const getUI = () => {
    return (
      <>
        <Header />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/resolutions"
            element={isAuthenticated() ? (<ModuleGuard form="RESOLUTION"><Resolutions /></ModuleGuard>) : <Login />}
          >
            <Route path=":id" />
          </Route>
          <Route path="/" element={isAuthenticated() ? <Home /> : <Login />} />
          <Route
            path="/home"
            element={isAuthenticated() ? <Home /> : <Login />}
          />
          <Route
            path="/operational-area"
            element={isAuthenticated() ? (<ModuleGuard form="OPERATIVE_AREA"><OperationalArea /></ModuleGuard>) : <Login />}
          >
            <Route path=":id" />
          </Route>
          <Route
            path="/vehicles"
            element={isAuthenticated() ? (<ModuleGuard form="VEHICLE"><Vehicle /></ModuleGuard>) : <Login />}
          >
            <Route path=":id" />
          </Route>
          <Route
            path="/vehicletypes"
            element={isAuthenticated() ? (<ModuleGuard form="VEHICLE_TYPE"><VehicleType /></ModuleGuard>) : <Login />}
          >
            <Route path=":id" />
          </Route>
          <Route
            path="/additionalnewness"
            element={isAuthenticated() ? (<ModuleGuard form="ADDITIONAL_NEWNESS"><AdditionalNewnessList /></ModuleGuard>) : <Login />}
          >
            <Route path=":id" />
          </Route>
          <Route
            path="/newness"
            element={isAuthenticated() ? (<ModuleGuard form="REPORTED_NEWNESS"><NewnessList /></ModuleGuard>) : <Login />}
          >
            <Route path=":id" />
          </Route>
          <Route
            path="/workflow"
            element={isAuthenticated() ? (<ModuleGuard form="WORKFLOW"><WorkFlow /></ModuleGuard>) : <Login />}
          >
            <Route path=":id" />
          </Route>
          <Route
            path="/dynamicforms"
            element={isAuthenticated() ? (<ModuleGuard form="DYNAMIC_FORM"><DynamicForm /></ModuleGuard>) : <Login />}
          />
          <Route
            path="/dynamicforms/:id"
            element={isAuthenticated() ? (<ModuleGuard form="DYNAMIC_FORM"><FormBuilder /></ModuleGuard>) : <Login />}
          />
          <Route
            path="/formfields"
            element={isAuthenticated() ? (<ModuleGuard form="FORM_FIELD"><FormFields /></ModuleGuard>) : <Login />}
          >
            <Route path=":id" />
          </Route>
          <Route
            path="/status"
            element={isAuthenticated() ? (<ModuleGuard form="EXECUTION_STATUS"><EventStatus /></ModuleGuard>) : <Login />}
          >
            <Route path=":id" />
          </Route>
          <Route
            path="/eventtype"
            element={isAuthenticated() ? (<ModuleGuard form="EVENT_TYPE"><EventType /></ModuleGuard>) : <Login />}
          >
            <Route path=":id" />
          </Route>
          <Route
            path="/personal"
            element={isAuthenticated() ? (<ModuleGuard form="PERSONAL"><Personal /></ModuleGuard>) : <Login />}
          >
            <Route path=":id" />
          </Route>
          <Route
            path="/contractor"
            element={isAuthenticated() ? (<ModuleGuard form="CONTRACTOR"><Contractor /></ModuleGuard>) : <Login />}
          >
            <Route path=":id" />
          </Route>
          <Route
            path="/provider"
            element={isAuthenticated() ? (<ModuleGuard form="PROVIDER"><Provider /></ModuleGuard>) : <Login />}
          >
            <Route path=":id" />
          </Route>
          <Route
            path="/client"
            element={isAuthenticated() ? (<ModuleGuard form="CLIENT"><Clients /></ModuleGuard>) : <Login />}
          >
            <Route path=":id" />
          </Route>
          <Route
            path="/measurementunit"
            element={isAuthenticated() ? (<ModuleGuard form="MEASUREMENT_UNIT"><MeasurementUnitList /></ModuleGuard>) : <Login />}
          >
            <Route path=":id" />
          </Route>
          <Route
            path="/supply"
            element={isAuthenticated() ? (<ModuleGuard form="SUPPLY"><SupplyList /></ModuleGuard>) : <Login />}
          >
            <Route path=":id" />
          </Route>
          <Route
            path="/position"
            element={isAuthenticated() ? (<ModuleGuard form="POSITION"><Position /></ModuleGuard>) : <Login />}
          >
            <Route path=":id" />
          </Route>
          <Route
            path="/profiles"
            element={isAuthenticated() ? (<ModuleGuard form="PROFILE"><Profiles /></ModuleGuard>) : <Login />}
          >
            <Route path=":id" />
          </Route>
          <Route
            path="/users"
            element={isAuthenticated() ? (<ModuleGuard form="USER"><Users /></ModuleGuard>) : <Login />}
          >
            <Route path=":id" />
          </Route>

          <Route
            path="/teams"
            element={isAuthenticated() ? <TeamsList /> : <Login />}
          />
          <Route
            path="/proceedings"
            element={isAuthenticated() ? (<ModuleGuard form="PROCEEDING"><Proceeding /></ModuleGuard>) : <Login />}
          />
          <Route
            path="/events"
            element={isAuthenticated() ? (<ModuleGuard form="EVENT"><Events /></ModuleGuard>) : <Login />}
          />
          <Route
            path="/events/my-services"
            element={isAuthenticated() ? (<ModuleGuard form="MY_SERVICES"><Events myServicesMode /></ModuleGuard>) : <Login />}
          />
          <Route
            path="/additionalCostType"
            element={isAuthenticated() ? (<ModuleGuard form="ADDITIONAL_COST_TYPE"><AdditionalCostTypes /></ModuleGuard>) : <Login />}
          />
          <Route
            path="/paymentType"
            element={isAuthenticated() ? (<ModuleGuard form="PAYMENT_TYPE"><PaymentTypes /></ModuleGuard>) : <Login />}
          />
          <Route
            path="/events/detail/:id"
            element={isAuthenticated() ? <EventDetail /> : <Login />}
          />
          <Route
            path="/events/detail/:id/report/:original"
            element={isAuthenticated() ? <Report /> : <Login />}
          />
          <Route
            path="/dailyRoute/:id/report"
            element={isAuthenticated() ? (<ModuleGuard form="DAILY_ROUTE"><PrintDailyRoute /></ModuleGuard>) : <Login />}
          />
          <Route
            path="/events/detail/:id/evidence/:evidenceId/:type"
            element={isAuthenticated() ? <EvidenceImageViewer /> : <Login />}
          />
          <Route
            path="/events/detail/:id/dynamic-form-field-value/:valueId/:original/:rotateImage"
            element={
              isAuthenticated() ? (
                <DynamicFormFieldValueImageViewer />
              ) : (
                <Login />
              )
            }
          />
          <Route
            path="/warehouse"
            element={isAuthenticated() ? (<ModuleGuard form="WAREHOUSE"><Warehouse /></ModuleGuard>) : <Login />}
          />
          <Route
            path="/preorder"
            element={isAuthenticated() ? (<ModuleGuard form="PRE_ORDER"><PreOrders /></ModuleGuard>) : <Login />}
          />
          <Route
            path="/evidencetemplates"
            element={isAuthenticated() ? (<ModuleGuard form="EVIDENCE_TEMPLATE"><EvidenceTemplate /></ModuleGuard>) : <Login />}
          />
          <Route
            path="/registerpayments"
            element={isAuthenticated() ? (<ModuleGuard form="REGISTER_PAYMENTS"><Payments /></ModuleGuard>) : <Login />}
          />
          <Route
            path="/dailyRoute"
            element={isAuthenticated() ? (<ModuleGuard form="DAILY_ROUTE"><DailyRoutes /></ModuleGuard>) : <Login />}
          />
          <Route
            path="/bills"
            element={isAuthenticated() ? (<ModuleGuard form="RELATED_BILLS"><Bill /></ModuleGuard>) : <Login />}
          />
          <Route
            path="/bills/new"
            element={isAuthenticated() ? (<ModuleGuard form="RELATED_BILLS"><BillCreateEdit /></ModuleGuard>) : <Login />}
          />
          <Route
            path="/bills/:id"
            element={isAuthenticated() ? (<ModuleGuard form="RELATED_BILLS"><BillCreateEdit /></ModuleGuard>) : <Login />}
          />
          <Route
            path="/inventoryMovementType"
            element={isAuthenticated() ? (<ModuleGuard form="INVENTORY_MOVEMENT_TYPE"><InventoryMovementType /></ModuleGuard>) : <Login />}
          />

          <Route
            path="/inventoryMovement"
            element={isAuthenticated() ? (<ModuleGuard form="INVENTORY_MOVEMENT"><InventoryMovement /></ModuleGuard>) : <Login />}
          />

          <Route
            path="/requestType"
            element={isAuthenticated() ? (<ModuleGuard form="REQUEST_TYPE"><RequestTypes /></ModuleGuard>) : <Login />}
          />

          <Route
            path="/reportType"
            element={isAuthenticated() ? (<ModuleGuard form="REPORT_TYPE"><ReportTypes /></ModuleGuard>) : <Login />}
          />

          <Route
            path="/finishConcept"
            element={isAuthenticated() ? (<ModuleGuard form="FINISH_CONCEPT"><FinishConcept /></ModuleGuard>) : <Login />}
          />

          <Route
            path="/generalRequests"
            element={isAuthenticated() ? (<ModuleGuard form="GENERAL_REQUESTS"><GeneralRequests /></ModuleGuard>) : <Login />}
          />
          <Route
            path="/supplyrequest"
            element={isAuthenticated() ? (<ModuleGuard form="SUPPLY_REQUEST"><SupplyRequestList /></ModuleGuard>) : <Login />}
          />
          <Route
            path="/additionalcostrequest"
            element={isAuthenticated() ? (<ModuleGuard form="ADDITIONAL_COST_REQUEST"><AdditionalCostRequestList /></ModuleGuard>) : <Login />}
          />
          <Route
            path="/paymenttotechnicianrequest"
            element={
              isAuthenticated() ? (<ModuleGuard form="PAYMENT_TO_TECHNICIAN_REQUEST"><PaymentToTechnicianRequestList /></ModuleGuard>) : <Login />
            }
          />
          <Route
            path="/settlement"
            element={isAuthenticated() ? (<ModuleGuard form="SETTLEMENT"><SettlementList /></ModuleGuard>) : <Login />}
          />
          <Route
            path="/settlement/report-all"
            element={isAuthenticated() ? (<ModuleGuard form="SETTLEMENT"><SettlementReportAll /></ModuleGuard>) : <Login />}
          />
          <Route
            path="/settlement/:uuid"
            element={isAuthenticated() ? (<ModuleGuard form="SETTLEMENT"><SettlementDetail /></ModuleGuard>) : <Login />}
          />
          <Route
            path="/settlement/:uuid/report"
            element={isAuthenticated() ? (<ModuleGuard form="SETTLEMENT"><SettlementReport /></ModuleGuard>) : <Login />}
          />
          <Route path="*" element={<NoMatch />} />
        </Routes>
      </>
    );
  };

  return (
    <>
      {getUI()}

      <Dialog
        open={showSessionExpiredModal}
        disableEscapeKeyDown
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AccessTime sx={{ color: "#10366E" }} />
            {localize.session_expired_title}
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.9rem" }}>
            {localize.session_expired_message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            sx={{ backgroundColor: "#10366E" }}
            onClick={handleSessionExpiredAccept}
          >
            {localize.accept}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default App;
